import React from "react";
import {
  createBulkFormAction,
  type CreateBulkFormActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import * as GQL from "src/types/graphql";
import type { BulkTargets } from "../types";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import { useBulkActionProgressToast } from "./useBulkActionProgressToast";

export type VerifyFormUpdate = {
  form_verification_id: uuid;
  verification_status: GQL.verification_status_enum;
};

function createBulkVerifyBody(
  targets: BulkTargets,
  updates: VerifyFormUpdate[]
): NonNullable<CreateBulkFormActionData["body"]>["data"] {
  return {
    ...targets,
    actionType: "VERIFY_FORM",
    actionData: { updates },
  };
}

export function useEnqueueBulkFormVerify(
  bulkTargets: BulkTargets | null,
  selectionSize: number
) {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();

  return React.useCallback(
    async (updates: VerifyFormUpdate[]): Promise<boolean> => {
      if (!bulkTargets) {
        toast({
          title: "No forms selected.",
          status: "info",
        });
        return false;
      }

      if (!updates.length) {
        toast({ title: "No changes made." });
        return false;
      }

      try {
        const response = throwIfFormServiceError(
          await createBulkFormAction({
            body: {
              data: createBulkVerifyBody(bulkTargets, updates),
            },
          })
        );

        trackFormAction({
          verb: "Queued",
          actionType: "VERIFY_FORM",
          bulk: true,
          count: selectionSize,
          async: true,
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: bulkTargets.formTemplateId,
          actionType: "VERIFY_FORM",
          selectionSize,
        });
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType: "VERIFY_FORM",
          bulk: true,
          count: selectionSize,
          async: true,
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [bulkTargets, selectionSize, toast, showProgressToast]
  );
}
